import { z } from 'zod';
import { readNullableString, readRequiredString } from '@/shared/wire';

const LocalDateInputSchema = z.union([
  z.string().min(1),
  z.tuple([z.number().int(), z.number().int(), z.number().int()]),
]);

function normalizeLocalDate(input: unknown): string {
  if (typeof input === 'string') {
    return input;
  }
  if (Array.isArray(input) && input.length === 3) {
    const [year, month, day] = input;
    if ([year, month, day].every((value) => typeof value === 'number' && Number.isFinite(value))) {
      return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  }
  return '';
}

function readLocalDateRecord(input: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const candidate = input[key];
    const parsed = LocalDateInputSchema.safeParse(candidate);
    if (parsed.success) {
      return normalizeLocalDate(parsed.data);
    }
  }
  return undefined;
}

function readLocalDateNullableRecord(input: Record<string, unknown>, ...keys: string[]): string | null | undefined {
  for (const key of keys) {
    const candidate = input[key];
    if (candidate == null) {
      return null;
    }
    const parsed = LocalDateInputSchema.safeParse(candidate);
    if (parsed.success) {
      return normalizeLocalDate(parsed.data);
    }
  }
  return undefined;
}

const InstantNullableSchema = z.string().nullable();

export const EodModeSchema = z.enum(['HALT_ON_ERROR', 'CONTINUE_ON_ERROR']);
export type EodMode = z.infer<typeof EodModeSchema>;

export const EodOperatorPayloadSchema = z.object({
  status_date: z.string().optional(),
  runs_as_of_date: z.string().optional(),
}).partial();

export type EodOperatorPayload = z.infer<typeof EodOperatorPayloadSchema>;

export const EodTriggerRequestSchema = z.object({
  eod_date: z.string().optional(),
  force: z.boolean(),
});

export type EodTriggerRequest = z.infer<typeof EodTriggerRequestSchema>;

export const EodRerunRequestSchema = z.object({
  as_of_date: z.string().min(1),
  reason: z.string().optional(),
});

export type EodRerunRequest = z.infer<typeof EodRerunRequestSchema>;

export const EodCatchUpRequestSchema = z.object({
  target_as_of_date: z.string().min(1),
});

export type EodCatchUpRequest = z.infer<typeof EodCatchUpRequestSchema>;

export const EodTriggerResponseSchema = z
  .custom<Record<string, unknown>>((value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value))
  .transform((value) => ({
    eod_date: readLocalDateNullableRecord(value, 'eod_date', 'eodDate'),
    mode: readRequiredString(value, 'mode') ?? '',
    message: readRequiredString(value, 'message') ?? '',
  }));

export type EodTriggerResponse = z.infer<typeof EodTriggerResponseSchema>;

export const EodCatchUpResponseSchema = z
  .custom<Record<string, unknown>>((value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value))
  .transform((value) => ({
    target_as_of_date: readLocalDateRecord(value, 'target_as_of_date', 'targetAsOfDate') ?? '',
    processed_dates: Array.isArray(value['processed_dates'])
      ? value['processed_dates'].map((item) => normalizeLocalDate(item)).filter((item) => item.length > 0)
      : Array.isArray(value['processedDates'])
        ? value['processedDates'].map((item) => normalizeLocalDate(item)).filter((item) => item.length > 0)
        : [],
    message: readRequiredString(value, 'message') ?? '',
  }));

export type EodCatchUpResponse = z.infer<typeof EodCatchUpResponseSchema>;

export const EodStepStatusSchema = z
  .custom<Record<string, unknown>>((value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value))
  .transform((value) => ({
    step: readRequiredString(value, 'step') ?? '',
    order: typeof value['order'] === 'number' ? value['order'] : 0,
    status: readRequiredString(value, 'status') ?? '',
    started_at: InstantNullableSchema.parse(readNullableString(value, 'started_at', 'startedAt') ?? null),
    completed_at: InstantNullableSchema.parse(readNullableString(value, 'completed_at', 'completedAt') ?? null),
    message: readNullableString(value, 'message') ?? null,
  }));

export type EodStepStatus = z.infer<typeof EodStepStatusSchema>;

export const EodStatusResponseSchema = z
  .custom<Record<string, unknown>>((value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value))
  .transform((value) => ({
    eod_date: readLocalDateRecord(value, 'eod_date', 'eodDate') ?? '',
    overall_status: readRequiredString(value, 'overall_status', 'overallStatus') ?? '',
    steps: z.array(EodStepStatusSchema).parse(value['steps'] ?? []),
  }));

export type EodStatusResponse = z.infer<typeof EodStatusResponseSchema>;

export const EodRunSchema = z
  .custom<Record<string, unknown>>((value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value))
  .transform((value) => ({
    run_id: readRequiredString(value, 'run_id', 'runId') ?? '',
    mode: readRequiredString(value, 'mode') ?? '',
    status: readRequiredString(value, 'status') ?? '',
    rerun: typeof value['rerun'] === 'boolean' ? value['rerun'] : false,
    catch_up: typeof value['catch_up'] === 'boolean'
      ? value['catch_up']
      : typeof value['catchUp'] === 'boolean'
        ? value['catchUp']
        : false,
    started_at: InstantNullableSchema.parse(readNullableString(value, 'started_at', 'startedAt') ?? null),
    completed_at: InstantNullableSchema.parse(readNullableString(value, 'completed_at', 'completedAt') ?? null),
    message: readNullableString(value, 'message') ?? null,
  }));

export type EodRun = z.infer<typeof EodRunSchema>;

export const EodRunHistoryResponseSchema = z
  .custom<Record<string, unknown>>((value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value))
  .transform((value) => ({
    as_of_date: readLocalDateRecord(value, 'as_of_date', 'asOfDate') ?? '',
    runs: z.array(EodRunSchema).parse(value['runs'] ?? []),
  }));

export type EodRunHistoryResponse = z.infer<typeof EodRunHistoryResponseSchema>;
